import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiCheck, FiHeart, FiShoppingBag } from "react-icons/fi";
import { formatCurrency } from "../../config/siteConfig";
import { useCommerce } from "../../hooks/useCommerce";
import Badge from "../common/Badge";
import Button from "../common/Button";
import RatingStars from "../common/RatingStars";
import classNames from "../common/classNames";

const imageSource = (image) => (typeof image === "string" ? image : image?.src || image?.url);
const imageAlt = (image, fallback) => (typeof image === "object" ? image?.alt || fallback : fallback);
const numericPrice = (price) => Number(price?.amount ?? price?.value ?? price) || 0;
const EMPTY_VARIANTS = [];
const FALLBACK_PRODUCT = {
  id: "unavailable-product",
  slug: "unavailable-product",
  name: "Unavailable product",
  price: 0,
};
const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const ProductCard = ({
  product = FALLBACK_PRODUCT,
  className,
  onQuickShop,
  showDescription = true,
  priority = false,
}) => {
  const commerce = useCommerce();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const images = Array.isArray(product.images)
    ? product.images
    : [product.image].filter(Boolean);
  const primaryImage = images[0];
  const secondaryImage = images[1];
  const variants = useMemo(
    () => (Array.isArray(product.variants) ? product.variants : EMPTY_VARIANTS),
    [product.variants]
  );
  const availableVariant =
    variants.find((variant) => variant.available !== false && Number(variant.stock ?? 1) > 0) ||
    variants[0] ||
    null;
  const isAvailable =
    product.available !== false &&
    Number(product.stock ?? 1) > 0 &&
    (!variants.length || Boolean(availableVariant?.available !== false && Number(availableVariant?.stock ?? 1) > 0));
  const productHref = `/products/${product.slug}`;
  const reviewCount = Array.isArray(product.reviews)
    ? product.reviews.length
    : Number(product.reviewCount ?? product.reviews ?? 0);
  const description = Array.isArray(product.description)
    ? product.description[0]
    : product.shortDescription || product.description;
  const wishlisted =
    commerce.isWishlisted?.(product.id) ||
    (commerce.wishlistItems || commerce.wishlist || []).some((item) =>
      typeof item === "object" ? item.id === product.id : item === product.id
    );

  const priceSummary = useMemo(() => {
    const variantPrices = variants.map((variant) => numericPrice(variant.price)).filter(Number.isFinite);
    const lowestPrice = variantPrices.length ? Math.min(...variantPrices) : numericPrice(product.price);
    const highestPrice = variantPrices.length ? Math.max(...variantPrices) : lowestPrice;
    return { lowestPrice, hasRange: highestPrice > lowestPrice };
  }, [product.price, variants]);

  const handleWishlist = () => commerce.toggleWishlist?.(product.id);

  const handleAction = async () => {
    if (onQuickShop) {
      onQuickShop(product);
      return;
    }
    if (variants.length > 1) return;

    setAdding(true);
    try {
      await commerce.addToCart?.(product, availableVariant, 1);
      setAdded(true);
      if (commerce.openCart) commerce.openCart();
      else if (commerce.openCartDrawer) commerce.openCartDrawer();
      else commerce.openDrawer?.("cart");
      window.setTimeout(() => setAdded(false), 1800);
    } finally {
      setAdding(false);
    }
  };

  const badge =
    product.badge || product.badges?.[0] || product.labels?.[0] || (product.shipsFree ? "Ships free" : null);

  return (
    <article className={classNames("product-card", !isAvailable && "product-card--sold-out", className)}>
      <div className="product-card__media">
        {badge && <Badge className="product-card__badge" tone="accent">{badge}</Badge>}
        <button
          className={classNames("product-card__wishlist", wishlisted && "product-card__wishlist--active")}
          type="button"
          onClick={handleWishlist}
          aria-pressed={Boolean(wishlisted)}
          aria-label={`${wishlisted ? "Remove" : "Add"} ${product.name} ${wishlisted ? "from" : "to"} wishlist`}
        >
          <FiHeart aria-hidden="true" />
        </button>
        <Link to={productHref} className="product-card__image-link" aria-label={`View ${product.name}`}>
          {primaryImage ? (
            <>
              <img
                className="product-card__image product-card__image--primary"
                src={imageSource(primaryImage)}
                alt={imageAlt(primaryImage, product.name)}
                loading={priority ? "eager" : "lazy"}
              />
              {secondaryImage && (
                <img
                  className="product-card__image product-card__image--secondary"
                  src={imageSource(secondaryImage)}
                  alt=""
                  loading="lazy"
                />
              )}
            </>
          ) : (
            <span className="product-card__image-placeholder" aria-hidden="true">
              <FiShoppingBag />
            </span>
          )}
        </Link>
      </div>

      <div className="product-card__content">
        {product.category && (
          <Link
            className="product-card__category"
            to={`/collections/${product.categorySlug || slugify(product.category)}`}
          >
            {product.category}
          </Link>
        )}
        <h3 className="product-card__title">
          <Link to={productHref}>{product.name}</Link>
        </h3>
        {(product.rating || reviewCount > 0) && (
          <RatingStars rating={product.rating} count={reviewCount} size="small" />
        )}
        <div className="product-card__price">
          <span>
            {priceSummary.hasRange && <small>From </small>}
            {formatCurrency(priceSummary.lowestPrice)}
          </span>
          {numericPrice(product.compareAtPrice) > priceSummary.lowestPrice && (
            <del>{formatCurrency(numericPrice(product.compareAtPrice))}</del>
          )}
        </div>
        {showDescription && description && <p className="product-card__description">{description}</p>}
        <div className="product-card__actions">
          {variants.length > 1 && !onQuickShop ? (
            <Button fullWidth variant="secondary" to={productHref}>
              Select options
            </Button>
          ) : (
            <Button
              fullWidth
              type="button"
              variant="secondary"
              onClick={handleAction}
              disabled={!isAvailable}
              loading={adding}
              loadingLabel="Adding"
            >
              {added ? (
                <>
                  <FiCheck aria-hidden="true" /> Added
                </>
              ) : isAvailable ? (
                onQuickShop ? "Select size" : "Add to cart"
              ) : (
                "Sold out"
              )}
            </Button>
          )}
        </div>
        <span className="visually-hidden" role="status" aria-live="polite">
          {added ? `${product.name} added to cart` : ""}
        </span>
      </div>
    </article>
  );
};

export default ProductCard;
